
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Crown, Trophy, Medal, Users, TrendingUp } from "lucide-react";
import { useSalesAnalytics } from "@/hooks/useSalesAnalytics";

const TopEmployeesRanking = () => {
  const { data, isLoading } = useSalesAnalytics();

  if (isLoading) {
    return (
      <Card className="bg-white/80 backdrop-blur-sm border-slate-200/60">
        <CardContent className="p-6">
          <div className="animate-pulse space-y-4">
            <div className="h-4 bg-gray-200 rounded w-1/2"></div>
            <div className="space-y-3">
              {[...Array(5)].map((_, i) => (
                <div key={i} className="h-20 bg-gray-200 rounded"></div>
              ))}
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  const getRankIcon = (index: number) => {
    switch (index) {
      case 0: return <Crown className="w-5 h-5 text-yellow-500" />;
      case 1: return <Trophy className="w-5 h-5 text-gray-400" />;
      case 2: return <Medal className="w-5 h-5 text-amber-600" />;
      default: return <div className="w-5 h-5 bg-slate-400 rounded-full flex items-center justify-center text-white text-xs font-bold">{index + 1}</div>;
    }
  };

  const getRankBg = (index: number) => {
    switch (index) {
      case 0: return "bg-gradient-to-r from-yellow-50 to-yellow-100 border-yellow-200";
      case 1: return "bg-gradient-to-r from-gray-50 to-gray-100 border-gray-200";
      case 2: return "bg-gradient-to-r from-amber-50 to-amber-100 border-amber-200";
      default: return "bg-gradient-to-r from-blue-50 to-blue-100 border-blue-200";
    }
  };

  return (
    <Card className="bg-white/80 backdrop-blur-sm border-slate-200/60">
      <CardHeader>
        <CardTitle className="text-xl font-bold text-slate-800 flex items-center gap-2">
          <Users className="w-5 h-5 text-purple-600" />
          ترتيب أفضل الموظفين
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          {data?.topEmployees?.map((employee, index) => (
            <div key={employee.name} className={`p-4 rounded-lg border ${getRankBg(index)} hover:shadow-md transition-all`}>
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-3">
                  {getRankIcon(index)}
                  <div>
                    <h3 className="font-semibold text-slate-800">{employee.name}</h3>
                    <p className="text-sm text-slate-600">{employee.department}</p>
                  </div>
                </div>
                <div className="text-left">
                  <Badge variant="secondary" className="text-green-700 bg-green-100 mb-1">
                    {employee.total.toLocaleString('ar-SA')} ر.س
                  </Badge>
                  <p className="text-xs text-slate-500">{employee.count} حجز</p>
                </div>
              </div>
              
              <div className="flex items-center justify-between text-sm">
                <div className="flex items-center gap-2 text-slate-600">
                  <TrendingUp className="w-4 h-4" />
                  <span>متوسط الحجز: {employee.average.toLocaleString('ar-SA')} ر.س</span>
                </div>
                <div className="w-24 bg-gray-200 rounded-full h-2">
                  <div 
                    className="bg-gradient-to-r from-blue-500 to-purple-500 h-2 rounded-full transition-all"
                    style={{ 
                      width: `${Math.min((employee.total / (data?.topEmployees?.[0]?.total || 1)) * 100, 100)}%` 
                    }}
                  ></div>
                </div>
              </div>
            </div>
          ))}
          
          {(!data?.topEmployees || data.topEmployees.length === 0) && (
            <div className="text-center py-8 text-slate-500">
              <Users className="w-12 h-12 mx-auto mb-3 text-slate-300" />
              <p>لا توجد بيانات مبيعات متاحة</p>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export default TopEmployeesRanking;
